/**
 * HumanRig — the player's cutout puppet, one of Datou's two human companions
 * (Mei or An) in a selectable shared-life outfit and age.
 * Billboarded plates — head, torso (with the leash arm + outfit), two legs —
 * driven by a simple two-beat walk cycle, breathing idle, and facing flip.
 *
 * Head and torso exist in TWO views: front (facing camera) and a true profile.
 * Walking across the screen shows the profile (mirrored for leftward); walking
 * toward/away or standing shows the front — so the walker turns to face you
 * when she stops. Plate sizes and stacking come from `plateLayout(age)`, so
 * kid / teen / adult share proportions (head stays big, limbs stretch). The
 * torso plate reports its drawn leash-hand position so the rope attaches
 * exactly in either view.
 */

import * as THREE from 'three';
import {
  drawWalkerHead,
  drawWalkerLeg,
  drawWalkerTorso,
  plateLayout,
  type PlateLayout,
  type ViewId,
} from '../art/walkerParts';
import { canvasTexture } from '../art/textures';
import type { AgeId, CharId, DirId } from '../art/walkerData';
import type { PropSprite } from '../art/props';

function partPlane(
  sprite: PropSprite,
  height: number,
  anchor: 'top' | 'bottom',
  tint = 1,
): THREE.Mesh {
  const w = height * sprite.aspect;
  const geo = new THREE.PlaneGeometry(w, height);
  geo.translate(0, anchor === 'top' ? -height / 2 : height / 2, 0);
  const mat = new THREE.MeshBasicMaterial({
    map: canvasTexture(sprite.canvas),
    transparent: true,
    alphaTest: 0.08,
    depthWrite: true,
    side: THREE.DoubleSide,
  });
  mat.color.setScalar(tint);
  return new THREE.Mesh(geo, mat);
}

/** One drawn view of the upper body: its two plates + the leash-hand anchor. */
interface BodyView {
  torso: THREE.Mesh;
  head: THREE.Mesh;
  hand: THREE.Vector3;
}

const VIEWS: ViewId[] = ['front', 'side'];

export class HumanRig {
  readonly group = new THREE.Group();

  private readonly flip = new THREE.Group();
  private bodies!: Record<ViewId, BodyView>;
  private nearLeg!: THREE.Mesh;
  private farLeg!: THREE.Mesh;
  private readonly shadow: THREE.Mesh;

  private char: CharId;
  private dir: DirId;
  private age: AgeId;
  private layout: PlateLayout;
  private readonly handWorld = new THREE.Vector3();

  private time = 0;

  constructor(
    shadowTexture: THREE.Texture,
    char: CharId = 'mei',
    dir: DirId = 'scout',
    age: AgeId = 'adult',
  ) {
    this.char = char;
    this.dir = dir;
    this.age = age;
    this.layout = plateLayout(age);
    this.buildPlates();
    this.group.add(this.flip);

    this.shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1.1, 0.7),
      new THREE.MeshBasicMaterial({ map: shadowTexture, transparent: true, depthWrite: false }),
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.position.y = 0.006;
    this.shadow.renderOrder = 0.5;
    this.group.add(this.shadow);
  }

  private makeLeg(tint: number): THREE.Mesh {
    return partPlane(drawWalkerLeg(this.char, this.dir, this.age), this.layout.legH, 'top', tint);
  }

  private makeBody(view: ViewId): BodyView {
    const sprite = drawWalkerTorso(this.char, this.dir, this.age, view);
    const torso = partPlane(sprite, this.layout.torsoH, 'bottom');
    // Map the plate's reported hand fraction into the torso's local space.
    // Plate is bottom-anchored: v=1 → bottom (torsoBottomY), v=0 → top.
    const w = this.layout.torsoH * sprite.aspect;
    const hand = new THREE.Vector3(
      (sprite.hand.u - 0.5) * w,
      this.layout.torsoBottomY + (1 - sprite.hand.v) * this.layout.torsoH,
      0.05,
    );
    const head = partPlane(drawWalkerHead(this.char, this.dir, this.age, view), this.layout.headH, 'bottom');
    return { torso, head, hand };
  }

  /** Draw legs + both body views, add them to the rig, and lay everything out. */
  private buildPlates(): void {
    this.farLeg = this.makeLeg(0.82);
    this.nearLeg = this.makeLeg(1);
    this.bodies = { front: this.makeBody('front'), side: this.makeBody('side') };
    this.flip.add(this.farLeg, this.nearLeg);
    for (const v of VIEWS) this.flip.add(this.bodies[v].torso, this.bodies[v].head);
    this.placePlates();
    this.applyView();
    this.buildSharon();
  }

  private readonly shrimpGroup = new THREE.Group();
  private shrimpSpriteMesh!: THREE.Mesh;
  private walkCycle = 0;
  private facing = 1;

  private buildSharon(): void {
    // Hide original 2D human cutout plates
    this.farLeg.visible = false;
    this.nearLeg.visible = false;
    for (const v of VIEWS) {
      this.bodies[v].torso.visible = false;
      this.bodies[v].head.visible = false;
    }

    const loader = new THREE.TextureLoader();
    const texPath = this.char === 'an' ? './tapwater/standing_side34.png' : './sharon/standing_side34.png';
    const charTex = loader.load(texPath);
    charTex.colorSpace = THREE.SRGBColorSpace;

    const width = this.char === 'an' ? 1.0 : 1.25;
    const height = this.char === 'an' ? 2.25 : 1.7;

    const geo = new THREE.PlaneGeometry(width, height);
    geo.translate(0, height / 2, 0);

    const mat = new THREE.MeshBasicMaterial({
      map: charTex,
      transparent: true,
      alphaTest: 0.05,
      depthWrite: true,
      side: THREE.DoubleSide,
    });

    if (this.shrimpSpriteMesh) {
      this.shrimpGroup.remove(this.shrimpSpriteMesh);
      if (this.shrimpSpriteMesh.geometry) this.shrimpSpriteMesh.geometry.dispose();
      const oldMat = this.shrimpSpriteMesh.material as THREE.MeshBasicMaterial;
      if (oldMat.map) oldMat.map.dispose();
      oldMat.dispose();
    }

    this.shrimpSpriteMesh = new THREE.Mesh(geo, mat);
    this.shrimpGroup.add(this.shrimpSpriteMesh);
    if (!this.flip.children.includes(this.shrimpGroup)) {
      this.flip.add(this.shrimpGroup);
    }
  }

  /** Position every plate from the current age's layout (call after any build). */
  private placePlates(): void {
    const L = this.layout;
    this.farLeg.position.set(-0.05, L.hipY, -0.03);
    this.nearLeg.position.set(0.06, L.hipY, 0.03);
    for (const v of VIEWS) {
      this.bodies[v].torso.position.set(0, L.torsoBottomY, 0.01);
      this.bodies[v].head.position.set(v === 'side' ? 0 : 0.02, L.headY, 0.02);
    }
  }

  private applyView(): void {
    for (const v of VIEWS) {
      this.bodies[v].torso.visible = false;
      this.bodies[v].head.visible = false;
    }
    this.farLeg.visible = false;
    this.nearLeg.visible = false;
  }

  /** Swap the human companion (Mei/An -> Sharon/Tapwater) live — redraws character sprite. */
  setCharacter(char: CharId): void {
    if (char === this.char) return;
    this.char = char;
    this.rebuild();
  }

  /** Swap the walker's outfit direction live — redraws all plates. */
  setOutfit(dir: DirId): void {
    if (dir === this.dir) return;
    this.dir = dir;
    this.rebuild();
  }

  /** Swap the walker's age (kid/teen/adult) live — redraws + re-lays plates. */
  setAge(age: AgeId): void {
    if (age === this.age) return;
    this.age = age;
    this.layout = plateLayout(age);
    this.rebuild();
  }

  /** Re-draw every plate after a character / outfit / age change. */
  private rebuild(): void {
    if (this.shrimpSpriteMesh) {
      this.shrimpGroup.remove(this.shrimpSpriteMesh);
      if (this.shrimpSpriteMesh.geometry) this.shrimpSpriteMesh.geometry.dispose();
      const oldMat = this.shrimpSpriteMesh.material as THREE.MeshBasicMaterial;
      if (oldMat.map) oldMat.map.dispose();
      oldMat.dispose();
    }

    const drop = (old: THREE.Mesh): void => {
      this.flip.remove(old);
      const mat = old.material as THREE.MeshBasicMaterial;
      mat.map?.dispose();
      mat.dispose();
      old.geometry.dispose();
    };
    const nearRot = this.nearLeg.rotation.z;
    const farRot = this.farLeg.rotation.z;
    drop(this.farLeg);
    drop(this.nearLeg);
    for (const v of VIEWS) {
      drop(this.bodies[v].torso);
      drop(this.bodies[v].head);
    }
    this.buildPlates();
    this.nearLeg.rotation.z = nearRot;
    this.farLeg.rotation.z = farRot;
  }

  /** World position of the leash hand (for the rope), in the active view. */
  get handPosition(): THREE.Vector3 {
    const handY = this.char === 'an' ? 1.05 : 0.85;
    const handX = (this.char === 'an' ? 0.28 : 0.25) * this.facing;
    this.handWorld
      .set(handX, handY, 0.1)
      .applyAxisAngle(new THREE.Vector3(0, 1, 0), this.flip.rotation.y)
      .add(this.group.position);
    return this.handWorld;
  }

  update(dt: number, x: number, z: number, vx: number, vz: number, camYaw: number): void {
    this.time += dt;
    this.group.position.set(x, 0, z);
    this.flip.rotation.y = camYaw;

    const rightX = Math.cos(camYaw);
    const rightZ = -Math.sin(camYaw);
    const vScreen = vx * rightX + vz * rightZ;
    if (Math.abs(vScreen) > 0.12) {
      this.facing = vScreen > 0 ? 1 : -1;
    }
    this.flip.scale.x = this.facing;

    const speed = Math.hypot(vx, vz);
    const moving = speed > 0.1;

    if (moving) {
      this.walkCycle += dt * 12;
      this.shrimpGroup.rotation.z = Math.sin(this.walkCycle) * 0.08;
      this.shrimpGroup.position.y = Math.abs(Math.sin(this.walkCycle)) * 0.06;
    } else {
      const idleTime = this.time * 2.5;
      this.shrimpGroup.rotation.z = Math.sin(idleTime * 0.5) * 0.02;
      this.shrimpGroup.position.y = Math.sin(idleTime) * 0.015;
    }
  }
}

